import { createContext, useContext, type ReactNode } from 'react';

export type ToastVariant = 'success' | 'info' | 'warning' | 'error';

export interface ToastOptions {
  /** Auto-dismiss after this many ms. `null` to disable. Defaults to 5000. */
  duration?: number | null;
  /** Stable key to de-dupe repeat toasts. */
  key?: string;
}

export interface ToastController {
  success: (message: ReactNode, opts?: ToastOptions) => number;
  info: (message: ReactNode, opts?: ToastOptions) => number;
  warn: (message: ReactNode, opts?: ToastOptions) => number;
  error: (message: ReactNode, opts?: ToastOptions) => number;
  dismiss: (id: number) => void;
  dismissAll: () => void;
}

export const ToastContext = createContext<ToastController | null>(null);

/**
 * Hook to enqueue toasts. Throws if used outside of a {@link ToastProvider}.
 *
 * @example
 *   const { success, error } = useToast();
 *   success('Saved');
 */
export function useToast(): ToastController {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}
